import { useQuery } from '@apollo/client';
import {
  Button,
  Toolbar,
  ToolbarButton,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  FluentProvider,
  webLightTheme,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  DocumentCheckmarkRegular,
  ChevronDownRegular,
  AddRegular,
  DocumentProhibitedRegular,
} from '@fluentui/react-icons';
import { INavLink, INavLinkGroup } from '@m365-admin/nav';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  bodyContentContainer,
  bodyContentWrapper,
} from '../../../app/common/styles/CommonStyleObjects';
import { GET_PROCESS_TEMPLATE_BY_TYPE } from '../../../utils/statApi/ProcessTemplateApi';
import { IProcessTemplate } from '../../../utils/types/IProcessTemplate';
import BackButton from '../../common/BackButton';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import BodyHeaderWithCommandBar from '../../common/headers/BodyHeaderWithCommandBar';
import FullWidthHeader from '../../common/headers/FullWidthHeader';
import MainDetailsView from '../../common/mainDetail/MainDetailsView';
import { IProcessTemplateTypeParams } from '../../common/ParamTypes';
import ProcessTemplateAddPanel from './ProcessTemplateAddPanel';
import ProcessTemplateDetails from './ProcessTemplateDetails';

const useStyles = makeStyles({
  toolbar: {
    padding: '0',
    background: tokens.colorNeutralBackground1,
  },
});

const ProcessTemplatesListPage = (): JSX.Element => {
  const styles = useStyles();
  const { processTypeId, processTemplateId } = useParams<IProcessTemplateTypeParams>();
  const [emptyResults, setEmptyResults] = useState(false);
  const [isActiveFilter, setIsActiveFilter] = useState(true);
  const [isAddProcessTemplatePanelOpen, setIsAddProcessTemplatePanelOpen] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const { loading, data, error, refetch } = useQuery(GET_PROCESS_TEMPLATE_BY_TYPE, {
    variables: { processTypeId: parseInt(processTypeId, 10), isActiveFilter: true },
    fetchPolicy: 'cache-and-network',
  });

  const processTemplatesByType: IProcessTemplate[] = useMemo(
    () => data?.processTemplatesByType ?? [],
    [data],
  );
  const processTemplateTypeName =
    !loading && processTemplatesByType ? processTemplatesByType[0]?.processType?.name : null;

  useEffect(() => {
    setEmptyResults(false);
    if (!loading) {
      const firstProcessTemplateId = data?.processTemplatesByType[0]?.id;
      const selectedTemplateId = parseInt(processTemplateId, 10);
      const hasValidSelectedTemplate =
        !!processTemplateId &&
        processTemplatesByType.some((processTemplate) => processTemplate.id === selectedTemplateId);

      if (!hasValidSelectedTemplate && firstProcessTemplateId) {
        history.replace(`/process-templates/${processTypeId}/template/${firstProcessTemplateId}`);
      } else if (!firstProcessTemplateId) {
        setEmptyResults(true);
      }
    }
  }, [data, loading, processTemplateId, processTypeId, history]);

  const uri = location.pathname.replace(/^\/|\/$/g, '');
  const pieces = uri.split('/');
  const activeBaseSegment = pieces.slice(0, 2);
  const baseUrl = activeBaseSegment.join('/');

  const getNavLinkGroups = (): INavLinkGroup[] => {
    const processTemplateNavLinks = processTemplatesByType.map(
      (processTemplate: IProcessTemplate, index: number) => {
        return {
          key: processTemplate.id.toString(),
          name: processTemplate.name,
          ariaLabel: processTemplate.name,
          url: processTemplate.id.toString(),
          isSelected: processTemplate.id.toString() === processTemplateId,
          onClick: (ev: React.MouseEvent<HTMLElement>, item: INavLink): void => {
            ev.preventDefault();
            ev.stopPropagation();
            history.push(`/${baseUrl}/template/${processTemplate.id}`);
          },
        } as INavLink;
      },
    );

    return [{ key: 'processTemplates', links: processTemplateNavLinks }];
  };
  const getHeaderTitle = (): JSX.Element => {
    return <h1>Template Management</h1>;
  };

  const onFilterUpdate = (isActive: boolean): void => {
    history.replace(`/process-templates/${processTypeId}`);
    refetch({
      processTypeId: parseInt(processTypeId, 10),
      isActiveFilter: isActive,
    });
    setIsActiveFilter(isActive);
  };

  const getTemplateMenuItems = () => [
    {
      key: 'Active',
      text: 'Active Templates',
      icon: <DocumentCheckmarkRegular />,
      onClick: () => {
        onFilterUpdate(true);
      },
    },
    {
      key: 'Deactivated',
      text: 'Deactivated Templates',
      icon: <DocumentProhibitedRegular />,
      onClick: () => {
        onFilterUpdate(false);
      },
    },
  ];

  const buttonText = isActiveFilter ? 'Active' : 'Deactivated';

  const getBackButton = (): JSX.Element => {
    return (
      <BackButton ariaLabel="Back to process types list" goToUrl="/process-templates">
        <h2>{`${
          loading
            ? ''
            : `Process Templates ${processTemplateTypeName ? `for ${processTemplateTypeName}` : ''}`
        }`}</h2>
      </BackButton>
    );
  };

  /** We need to find a way to get the process type title in the child view */
  // const getBodyHeaderTitle = (): JSX.Element => {
  //   return <Subheader title="Need to get process type title" />;
  // };

  const getCurrentProcessTemplate = () => {
    return processTemplatesByType.find((processTemplate) => {
      return processTemplate.id === parseInt(processTemplateId, 10);
    });
  };
  const currentProcessTemplate = getCurrentProcessTemplate();
  const getNavSectionHeader = (): JSX.Element => {
    return (
      <>
        {isActiveFilter && (
          <FluentProvider theme={webLightTheme}>
            <Toolbar
              className={styles.toolbar}
              aria-label="Use left and right arrow keys to navigate between commands"
            >
              <ToolbarButton
                icon={<AddRegular />}
                onClick={() => setIsAddProcessTemplatePanelOpen(true)}
                appearance="subtle"
              >
                Add Process Template
              </ToolbarButton>
            </Toolbar>
          </FluentProvider>
        )}
      </>
    );
  };

  return (
    <>
      <FullWidthHeader title={getHeaderTitle} />
      <div className={`${bodyContentContainer}  ms-depth-4`}>
        <div className={bodyContentWrapper}>
          <BodyHeaderWithCommandBar title={getBackButton}>
            <FluentProvider theme={webLightTheme}>
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    icon={<DocumentCheckmarkRegular />}
                    appearance="subtle"
                    aria-label={`${buttonText} Templates, select option to filter by status`}
                  >
                    {`${buttonText} Templates`}
                    <ChevronDownRegular />
                  </Button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {getTemplateMenuItems().map((item) => (
                      <MenuItem key={item.key} onClick={item.onClick}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {item.icon}
                          <span>{item.text}</span>
                        </div>
                      </MenuItem>
                    ))}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </FluentProvider>
          </BodyHeaderWithCommandBar>
          {emptyResults && (
            <div className={bodyContentWrapper}>
              <p>
                {`There are no ${buttonText.toLowerCase()} process templates for the current process type`}
              </p>
              {isActiveFilter && (
                <Button
                  icon={<AddRegular />}
                  appearance="primary"
                  onClick={() => {
                    setIsAddProcessTemplatePanelOpen(true);
                  }}
                >
                  Add Process Template
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      <LoadingErrorMessage
        loading={loading}
        error={error}
        actionName="loading process templates from database"
      />
      {processTemplateId && processTemplatesByType.length > 0 && currentProcessTemplate && (
        <MainDetailsView
          navLinkGroups={getNavLinkGroups()}
          navSectionHeader={getNavSectionHeader()}
          navAriaLabel="Process templates navigation"
        >
          <ProcessTemplateDetails processTemplateDetail={currentProcessTemplate} />
        </MainDetailsView>
      )}
      {isAddProcessTemplatePanelOpen && (
        <ProcessTemplateAddPanel
          closePanel={() => {
            setIsAddProcessTemplatePanelOpen(false);
          }}
        />
      )}
    </>
  );
};

export default ProcessTemplatesListPage;
